import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../PageHeader/PageHeader';
import { HitApi } from '../../Api/ApiHit';
import { searchWallet } from '../../constant/Constant';
import { useDispatch, useSelector } from 'react-redux';
import { setApiJson } from '../../redux/Actions/ApiAction';
import AppTable from '../../components/AppTable/AppTable';
import { setPagination } from '../../redux/Actions/TableAction';
import toast from 'react-hot-toast';
import TableSearch from '../../components/TableSearch/TableSearch';
import { hasPermission } from '../../utils/permissionUtils';

function WalletManagement() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const ApiReducer = useSelector((state) => state.ApiReducer);
    const TableDataReducer = useSelector((state) => state.TableReducer);

    // Search state variables
    const [optionSelect, setOptionSelect] = useState('');
    const [valueSearched, setValueSearched] = useState('');
    const [isSearching, setIsSearching] = useState(false);

    // Permission checks
    const canReadWallet = hasPermission('wallet', 'read');

    useEffect(() => {
        if (canReadWallet) {
            fetchWallet();
        }
    }, [TableDataReducer.pagination.page, TableDataReducer.pagination.limit, canReadWallet]);

    const fetchWallet = (searchKey, searchValue) => {
        if (!canReadWallet) {
            toast.error('You do not have permission to view wallets');
            return;
        }

        setIsSearching(true);

        const searchObj = {};

        // Only add search key/value if both are provided
        if (searchKey && searchValue) {
            searchObj[searchKey] = searchValue;
        }

        const json = {
            page: TableDataReducer.pagination.page,
            limit: TableDataReducer.pagination.limit,
            search: searchObj
        };

        HitApi(json, searchWallet).then((res) => {
            console.log("ressss", res);

            if (res?.statusCode == 200) {
                const walletData = Array.isArray(res?.data.docs) ? res?.data.docs : [];
                dispatch(setApiJson(walletData));

                const paginationData = {
                    total: res?.data?.totalDocs || 0,
                    page: TableDataReducer.pagination.page,
                    limit: TableDataReducer.pagination.limit
                };

                dispatch(setPagination(paginationData));
            } else {
                dispatch(setApiJson([]));
                toast.error('Failed to fetch wallets. Please try again.');
            }
        }).catch(error => {
            dispatch(setApiJson([]));
            toast.error('Error fetching wallets. Please try again.');
        }).finally(() => {
            setIsSearching(false);
        });
    };

    // Search functionality
    const handleSearch = () => {
        if (!canReadWallet) {
            toast.error('You do not have permission to search wallets');
            return;
        }

        if (!optionSelect) {
            toast.error('Please select a search option');
            return;
        }
        if (!valueSearched || valueSearched.trim() === '') {
            toast.error('Please enter a search value');
            return;
        }
        fetchWallet(optionSelect, valueSearched.trim());
    };

    // Clear search functionality
    const handleClearSearch = () => {
        if (!canReadWallet) {
            toast.error('You do not have permission to access wallets');
            return;
        }

        setOptionSelect('');
        setValueSearched('');
        fetchWallet();
    };

    // Search options
    const options = [
        { label: 'Client Name', value: 'name' },
        { label: 'Phone Number', value: 'phoneNumber' },
        { label: 'Status', value: 'isActive' },
    ];

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 2
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const renderStatus = (value, row) => {
        const isActive = row.isActive;
        const isFrozen = row.isFrozen;

        if (isFrozen) {
            return (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    Frozen
                </span>
            );
        }

        const bgColor = isActive ? "bg-green-100" : "bg-red-100";
        const textColor = isActive ? "text-green-800" : "text-red-800";
        const label = isActive ? "Active" : "Inactive";

        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
                {label}
            </span>
        );
    };

    const baseIndex = (TableDataReducer.pagination.page - 1) * TableDataReducer.pagination.limit;

    const tableHeaders = [
        {
            title: "S.No",
            key: "serialNumber",
            width: "60px",
            align: "center",
            render: (_, row, index) => {
                const serialNumber = baseIndex + index + 1;
                return (
                    <div className="text-center">
                        <span className="text-sm font-medium text-gray-900">{serialNumber}</span>
                    </div>
                );
            }
        },
        {
            title: "Client Name",
            key: "clientId",
            render: (value, row) => {
                const clientName = row.clientId?.name || 'N/A';
                return (
                    <div className="flex items-center">
                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center mr-2">
                            <span className="text-sm font-medium text-purple-600">
                                {clientName.charAt(0).toUpperCase()}
                            </span>
                        </div>
                        <span className="font-medium">{clientName}</span>
                    </div>
                );
            }
        },
        {
            title: "Phone Number",
            key: "phoneNumber",
            render: (value, row) => (
                <span className="text-sm text-gray-700">
                    {row.clientId?.phoneNumber || 'N/A'}
                </span>
            )
        },
        {
            title: "Balance",
            key: "balance",
            align: "start",
            render: (value) => (
                <span className="font-semibold text-blue-600">
                    {formatCurrency(value)}
                </span>
            )
        },
        {
            title: "Total Credits",
            key: "totalCredits",
            align: "start",
            render: (value) => (
                <span className="text-green-600">
                    {formatCurrency(value)}
                </span>
            )
        },
        {
            title: "Total Debits",
            key: "totalDebits",
            align: "start",
            render: (value) => (
                <span className="text-red-600">
                    {formatCurrency(value)}
                </span>
            )
        },
        {
            title: "Status",
            key: "status",
            align: "start",
            render: renderStatus
        },

    ];

    if (!canReadWallet) {
        return (
            <div className="p-5">
                <PageHeader
                    title={'Wallet Management'}
                    description={'Manage client wallets and transactions'}
                />
                <div className="flex justify-center items-center h-64">
                    <div className="text-center">
                        <h3 className="text-lg font-medium text-red-600 mb-2">Access Denied</h3>
                        <p className="text-gray-600">You don't have permission to view wallets.</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-5">
            <PageHeader
                title={'Wallet Management'}
                description={'Manage client wallets and transactions'}
            />
            <AppTable
                TH={tableHeaders}
                TD={Array.isArray(ApiReducer.apiJson) ? ApiReducer.apiJson : []}
                isLoading={!Array.isArray(ApiReducer.apiJson) || ApiReducer.apiJson.length === 0 || isSearching}
                emptyMessage="No wallets found"
                TableSearch={
                    <TableSearch
                        options={options}
                        onClick={handleSearch}
                        optionSelect={optionSelect}
                        setOptionSelect={setOptionSelect}
                        setValueSearched={setValueSearched}
                        valueSearched={valueSearched}
                        onClear={handleClearSearch}
                    />
                }
            />
        </div>
    );
}

export default WalletManagement;